// teleports.js — Pure helpers for the Teleportation Tile system.
//
// A teleport is a Firestore doc with two endpoints `a` and `b` plus a
// `twoWay` flag. Stepping on `a` snaps the token to `b`. If `twoWay`, the
// reverse fires too. Hidden trapdoors integrate with the same discovery
// plumbing as traps (passive perception watcher + active perception roll).

export const TELEPORT_STYLES = {
  'stairs-up':   { label: 'Stairs ▲',  icon: '▲', color: '#a3a3b8' },
  'stairs-down': { label: 'Stairs ▼',  icon: '▼', color: '#a3a3b8' },
  'pit':         { label: 'Pit',        icon: '⚫', color: '#1f1f24' },
  'portal':      { label: 'Portal',     icon: '◯', color: '#c084fc' },
  'trapdoor':    { label: 'Trapdoor',   icon: '⬡', color: '#8b5e3c' },
};

export function teleportStyleMeta(style) {
  return TELEPORT_STYLES[style] || TELEPORT_STYLES['portal'];
}

/** Does this teleport fire from (x, y)? Honors one-way semantics. */
export function triggersOnCell(teleport, x, y) {
  if (!teleport) return null;
  const a = teleport.a, b = teleport.b;
  if (!a || !b) return null;
  if (a.x === x && a.y === y) return { dest: b };
  if (teleport.twoWay && b.x === x && b.y === y) return { dest: a };
  return null;
}

/**
 * Find the first teleport that fires from (x, y), excluding any id in
 * `ignoreIds` (used as a per-drag loop-prevention set so the just-arrived
 * destination doesn't chain).
 */
export function findTeleportFromCell(teleports, x, y, ignoreIds = null) {
  for (const tp of Object.values(teleports || {})) {
    if (ignoreIds && ignoreIds.has(tp.id)) continue;
    const hit = triggersOnCell(tp, x, y);
    if (hit) return { teleport: tp, dest: hit.dest };
  }
  return null;
}

/** Midpoint between a and b — useful as an authoring preview anchor. */
export function teleportMidpoint(tp) {
  if (!tp?.a || !tp?.b) return null;
  return { x: (tp.a.x + tp.b.x) / 2, y: (tp.a.y + tp.b.y) / 2 };
}
